import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { QuestionCard } from "../shared/QuestionCard";
import { TrustpilotQuote } from "../shared/QuizSupport";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  /** Names of every dog on a fussy tier - the copy personalises to all of them. */
  fussyDogNames: string[];
};

/** "Coco", "Coco & Spot", "Coco, Spot & Rex" */
function joinNames(names: string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean);
  if (!clean.length) return "your dog";
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(", ")} & ${clean[clean.length - 1]}`;
}

const FUSSY_REVIEW = {
  title: "At last! Food that our dog Charlie loves!",
  quote:
    "Our dog Charlie has always been a fussy eater and used to get bored after a few weeks with what we gave him. Then we discovered Paws. It's been about 9 months now and he still absolutely loves his meals... He licks the bowl clean and it's a joy to see him loving his food so much.",
  author: "Verified Trustpilot review",
};

/**
 * Issue #52 B - reassurance interstitial, fired only when a dog is on one of
 * the two fussy tiers. Not a question: the footer's Next simply advances.
 */
export function FussyReassure({ fussyDogNames }: Props) {
  const dogs = joinNames(fussyDogNames);

  const TICKS: { text: string; bold?: boolean }[] = [
    { text: "Paws on Plates actually looks, smells and tastes like food." },
    { text: "Many dogs who snub kibble, wet and even other fresh foods dive straight into Paws." },
    // Joint possessive: apostrophe-s on the last name covers one dog and many.
    { text: `You can swap recipes anytime, so it's easy to land on ${dogs}'s favourites.` },
    { text: "Try completely risk-free with our 30-day money back guarantee", bold: true },
  ];

  return (
    <QuestionCard title="Fussy dog? No problem.">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          maxWidth: 460,
          mx: "auto",
          textAlign: "left",
        }}
      >
        {TICKS.map((tick) => (
          <Box key={tick.text} sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
            <Box
              sx={{
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: QUIZ.color.green,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: "1px",
              }}
            >
              <CheckIcon sx={{ fontSize: 16, color: QUIZ.color.ink }} />
            </Box>
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: 14.5, md: 15.5 },
                fontWeight: tick.bold ? 700 : 400,
                color: QUIZ.color.ink,
                lineHeight: 1.45,
              }}
            >
              {tick.text}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Further reassurance: a real 5-star fussy-eater review */}
      <Box sx={{ mt: { xs: 3, md: 4 }, display: "flex", justifyContent: "center" }}>
        <TrustpilotQuote {...FUSSY_REVIEW} />
      </Box>
    </QuestionCard>
  );
}
